import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router'
import { get, debounce } from 'lodash'
import { connect } from 'react-redux'
import { push } from 'connected-react-router'
import { compose } from 'recompose'

import {
  leaders as leadersApi,
  tasks as tasksApi,
  persons as personsApi,
  contacts as contactsApi
} from 'redux/modules/api'
import { setPage, notification, modal, logError } from 'redux/modules/app'
import { updateScheme, trainingScheme } from 'models/leaders'
import appConfig from 'config'
import mapLeadersForBulk from '../../../../redux/selectors/leaders/mapLeadersForBulk'
import areAllSelectedForBulk from '../../../../redux/selectors/awardLeaderAssignments/areAllSelectedForBulk'


function decorator(config = {}) {
  return ComposedComponent => {
    // leaders: api.organizations.leaders,
    const enhance = compose(
      withRouter,
      connect(state => ({
          leaders: state.api.leaders,
          user: state.auth.user,
          activeOrganizationId: state.auth.organization.id,
          local: {
            tasks: state.api.tasks.registrationAwardLeaders
          },
          commonLimit: state.app.commonLimit,
          selectableLeaderIds: config.list ? mapLeadersForBulk(state) : [],
          areAllLeadersSelected: config.list ? areAllSelectedForBulk(state) : false,
        }),
        {
          ...leadersApi,
          setPage,
          modal,
          push,
          notification,
          logError,
          uploadPersonImage: personsApi.uploadPersonImage,
          getProfile: personsApi.getProfile,
          deletePersonImage: personsApi.deletePersonImage,
          deleteOrganizationContact: contactsApi.deleteOrganizationContact,
          getTasksRegistrationAwardLeaders: tasksApi.registrationAwardLeaders
            .getTasksRegistrationAwardLeaders,
          getTasksOverview: tasksApi.overview.getTasksOverview,
          bulkSelectLeaders: leadersApi.toggleLeadersAllSelected
        }
      )
    )

    class Leaders extends Component {
      static propTypes = {
        user: PropTypes.object.isRequired,
        activeOrganizationId: PropTypes.number.isRequired,
        decoUpdateOrganizationOverview: PropTypes.bool,
        local: PropTypes.object.isRequired,
        leaders: PropTypes.object.isRequired,
        params: PropTypes.object,
        decoOrganizationId: PropTypes.number,
        getLeader: PropTypes.func.isRequired,
        getLeaders: PropTypes.func.isRequired,
        getLeadersForSearch: PropTypes.func.isRequired,
        transferLeaderParticipants: PropTypes.func.isRequired,
        uploadPersonImage: PropTypes.func.isRequired,
        deletePersonImage: PropTypes.func.isRequired,
        findLeader: PropTypes.func.isRequired,
        updateLeader: PropTypes.func.isRequired,
        addTraining: PropTypes.func.isRequired,
        updateTraining: PropTypes.func.isRequired,
        deleteTraining: PropTypes.func.isRequired,
        sendConfirmationEmail: PropTypes.func.isRequired,
        deleteOrganizationContact: PropTypes.func.isRequired,
        getTasksOverview: PropTypes.func.isRequired,
        moveLeader: PropTypes.func.isRequired,
        getProfile: PropTypes.func.isRequired,
        addHistoryRecord: PropTypes.func.isRequired,
        updateHistoryRecord: PropTypes.func.isRequired,
        deleteHistoryRecord: PropTypes.func.isRequired,
        exportLeaders: PropTypes.func.isRequired,
        setPage: PropTypes.func.isRequired,
        notification: PropTypes.func.isRequired,
        decoFilter: PropTypes.string,
        modal: PropTypes.func.isRequired,
        logError: PropTypes.func.isRequired,
        push: PropTypes.func.isRequired,
        approveAwardLeader: PropTypes.func.isRequired,
        hocShouldUpdatePersonDetail: PropTypes.bool,
        getTasksRegistrationAwardLeaders: PropTypes.func.isRequired,
        commonLimit: PropTypes.number,
        setLeadersParams: PropTypes.func,
        resetLeaderDetail: PropTypes.func.isRequired,
        selectableLeaderIds: PropTypes.list,
        areAllLeadersSelected: PropTypes.list,
        setLeadersSelected: PropTypes.func.isRequired
      }

      static defaultProps = {
        params: {}
      }

      constructor(props) {
        super(props)
        this.handleFindLeader = debounce(this.handleFindLeader, 600)
      }

      componentDidMount() {
        const { params, commonLimit, setLeadersParams } = this.props

        if (config.useCommonLimit && commonLimit) {
          setLeadersParams({ limit: commonLimit })
        }

        // commented out participants fetching, as calling it in both didMount and willReceiveProps functions was calling this fetch
        // and it caused race condition problem in some specific scenarios (see [SDNO-1063])
        // calling it only in willReceiveProps should be enough, as connect() should always cause execution of willReceiveProps function
        // config.list &&
        //   this.handleGetLeaders(leaders, this.handleGetBaseFilter())
        config.detail && params && params.id && this.handleGetLeader(params.id)
      }

      componentWillReceiveProps(nextProps) {
        const { leaders } = this.props
        const { filter, limit, offset, sort, invalidate } = nextProps.leaders

        if (
          leaders.filter !== filter ||
          leaders.limit !== limit ||
          leaders.offset !== offset ||
          leaders.sort !== sort ||
          leaders.invalidate !== invalidate
        ) {
          config.list &&
            this.handleGetLeaders(nextProps.leaders, this.handleGetBaseFilter())
        }
      }

      handleGetBaseFilter = id => {
        const { params, decoFilter, decoOrganizationId } = this.props

        switch (decoFilter || config.filter) {
          case 'awardUnitLeaders':
            return `organization.id=${id || decoOrganizationId || params.id}`
          case 'awardUnitRegistrations':
            return `organization.id=${params && params.id}`
          case 'coordinator':
            return 'isCoordinator=1'
          case 'organizationLeaders':
            return `organization.flatHierarchy.parentOrganization.id=${id || params.id}`
          default:
            return ''
        }
      }

      handleGetLeaders = (leaders, baseFilter, { onlyActive } = {}) =>
        this.props.getLeaders(leaders, baseFilter, {
          disableQueryFilter: config.disableQueryFilter,
          onlyActive: onlyActive || config.onlyActive
        })

      handleGetLeadersForSearch = (leaders, baseFilter, { onlyActive } = {}) =>
        this.props.getLeadersForSearch(leaders, baseFilter, {
          disableQueryFilter: config.disableQueryFilter,
          onlyActive: onlyActive || config.onlyActive
        })

      handleGetLeader = id => {
        this.props
          .getLeader(id, { readParams: config.detailReadParams })
          .then(({ body }) => {
            const leaderId = get(body, 'globalId') ? `(${body.globalId})` : ''

            this.props.setPage(
              `${get(body, 'person.firstName')} ${get(body, 'person.lastName')} ${leaderId}`,
              true
            )
          })
      }

      handleExportLeaders = () => {
        const { leaders } = this.props

        return this.props
          .exportLeaders(leaders, this.handleGetBaseFilter())
          .catch(this.handleError)
      }

      handleFindLeader = email => {
        const query = `person.email=${email}`

        this.props.findLeader(query)
      }

      handleLeaderUpdateBlob = fields => {
        if (fields.file) {
          this.props
            .uploadPersonImage(fields.person.id, fields.file)
            .then(() => {
              this.handleUpdateLeader(fields)
            })
            .catch(this.handleError)
          return
        }

        this.handleRemovePersonImage(fields)
        this.handleUpdateLeader(fields)
      }

      handleRemovePersonImage = fields => {
        if (
          get(this.props.leaders, 'detail.person.photoUrl') && !fields.photoUrl
        ) {
          this.props.deletePersonImage(get(fields, 'person.id')).then(() => {
            this.handleUpdateLeader(fields)
          })
          return
        }
      }

      handleUpdateLeader = fields => {
        const { params } = this.props

        this.props
          .updateLeader(get(params, 'id'), updateScheme(fields))
          .then(this.handleUpdateSuccess)
          .catch(this.handleError)
      }

      handleLeaderDelete = (leaderId, fields = {}) =>
        this.props
          .deleteOrganizationContact(leaderId, {
            reason: fields.reason
          })
          .then(this.handleRemoveSuccess)
          .catch(this.handleError)

      handleAddTraining = (fields, leaderId) => {
        const { params } = this.props
        const id = get(params, 'id') || leaderId

        return this.props
          .addTraining(id, trainingScheme(fields))
          .then(this.handleAddTrainingSuccess)
          .catch(this.handleError)
      }

      handleUpdateTraining = (fields, leaderId) => {
        const { params } = this.props
        const id = get(params, 'id') || leaderId

        return this.props
          .updateTraining(id, fields.id, fields)
          .then(this.handleUpdateTrainingSuccess)
          .catch(this.handleError)
      }

      handleDeleteTraining = (trainingId, leaderId) => {
        const { params } = this.props
        const id = get(params, 'id') || leaderId

        this.props
          .deleteTraining(id, trainingId)
          .then(this.handleDeleteSuccess)
          .catch(this.handleError)
      }

      handlAddHistoryRecord = (leaderId, fields) =>
        this.props
          .addHistoryRecord(leaderId, fields)
          .then(this.handleAddHistoryRecordSuccess)
          .catch(this.handleError)

      handlUpdateHistoryRecord = (leaderId, fields) =>
        this.props
          .updateHistoryRecord(leaderId, fields.recordId, fields)
          .then(this.handleUpdateHistoryRecordSuccess)
          .catch(this.handleError)

      handlDeleteHistoryRecord = (leaderId, recordId) =>
        this.props
          .deleteHistoryRecord(leaderId, recordId)
          .then(this.handleDeleteHistoryRecordSuccess)
          .catch(this.handleError)

      handleMoveLeader = awardUnitId => {
        const { params } = this.props

        this.props
          .moveLeader(params && params.id, awardUnitId)
          .then(this.handleMoveSuccess)
          .catch(this.handleError)
      }

      handleSendConfirmatioEmail = leaderId =>
        this.props
          .sendConfirmationEmail(leaderId)
          .then(this.handleSendConfirmationEmailSuccess)
          .catch(this.handleError)

      handleTransferLeaderParticipants = (fields = {}, oldLeaderId) => {
        this.props
          .transferLeaderParticipants(oldLeaderId, fields.leaderId)
          .then(this.handleParticipantTransferred)
          .catch(this.handleError)
      }

      handleApproveAwardLeaderRegistration = id =>
        this.props
          .approveAwardLeader(id, { approved: true })
          .then(this.handleApproveSuccess)
          .catch(this.handleError)

      handleReturnAwardLeaderRegistration = (id, fields) =>
        this.props
          .approveAwardLeader(id, { approved: false, note: fields.note })
          .then(this.handleReturnSuccess)
          .catch(this.handleError)

      handleApproveSuccess = () => {
        this.props.notification('Award Leader has been approved', {
          status: 'success',
          intlKey: 'notifications.award_leader_has_been_approved'
        })
        this.handleRefresh()
      }

      handleSendConfirmationEmailSuccess = () => {
        this.props.notification('Confirmation email has been sent', {
          status: 'success',
          intlKey: 'notifications.confirmation_email_has_been_sent'
        })
        this.handleRefresh()
      }

      // handleGetOrganizationId = () => get(this.props, 'params.id') || getActiveOrganizationId(this.props.user)

      handleUpdateSuccess = () => {
        this.props.notification('Award Leader has been updated', {
          status: 'success',
          intlKey: 'notifications.award_leader_has_been_updated'
        })
        this.handleRefresh()
      }

      handleRemoveSuccess = () => {
        this.props.notification('Award Leader has been removed', {
          status: 'success',
          intlKey: 'notifications.award_leader_has_been_removed'
        })
        this.props.push(`${appConfig.publicPath}/leaders`)
      }

      handleAddTrainingSuccess = () => {
        this.props.notification('Training has been added', {
          status: 'success',
          intlKey: 'notifications.training_has_been_added'
        })
        this.handleRefresh()
      }

      handleUpdateTrainingSuccess = () => {
        this.props.notification('Training has been updated', {
          status: 'success',
          intlKey: 'notifications.training_has_been_updated'
        })
        this.handleRefresh()
      }

      handleDeleteSuccess = () => {
        this.props.notification('Training has been removed', {
          status: 'success',
          intlKey: 'notifications.training_has_been_removed'
        })
        this.handleRefresh()
      }

      handleAddHistoryRecordSuccess = () => {
        this.props.notification('Guides record has been added', {
          status: 'success',
          intlKey: 'notifications.history_record_has_been_added'
        })
        this.handleRefresh()
      }

      handleUpdateHistoryRecordSuccess = () => {
        this.props.notification('Guides record has been updated', {
          status: 'success',
          intlKey: 'notifications.history_record_has_been_updated'
        })
        this.handleRefresh()
      }

      handleDeleteHistoryRecordSuccess = () => {
        this.props.notification('Guides record has been removed', {
          status: 'success',
          intlKey: 'notifications.history_record_has_been_removed'
        })
        this.handleRefresh()
      }

      handleParticipantTransferred = () => {
        this.props.notification('Participants have been transferred', {
          status: 'success',
          intlKey: 'notifications.participants_have_been_transferred'
        })
        this.handleRefresh()
        this.props.modal(null)
      }

      handleReturnSuccess = () => {
        this.props.notification('Award Leader has been returned', {
          status: 'success',
          intlKey: 'notifications.award_leader_has_been_returned'
        })
        this.handleRefresh()
      }

      handleMoveSuccess = () => {
        this.props.notification('Award Leader has been moved', {
          status: 'success',
          intlKey: 'notifications.award_leader_has_been_moved'
        })
        this.handleRefresh()
      }

      handleError = err => {
        console.log('error', err.body && err.body.userMessage)
        this.props.logError(err)
      }

      handleResetLeaderDetail = () =>
        this.props.resetLeaderDetail()

      handleRefresh = () => {
        const {
          local,
          getTasksRegistrationAwardLeaders,
          params,
          leaderId,
          hocShouldUpdatePersonDetail,
          decoUpdateOrganizationOverview
        } = this.props

        config.updateDetail &&
        !hocShouldUpdatePersonDetail &&
        this.handleGetLeader(params.id || leaderId)

        hocShouldUpdatePersonDetail &&
        this.props.getProfile({ readParams: config.personReadParams })

        let id = null

        if (decoUpdateOrganizationOverview) {
          id = params.id
        }
        config.updateTasks &&
        getTasksRegistrationAwardLeaders(
          local.tasks,
          this.handleGetBaseFilter()
        )
        this.props.getTasksOverview(null, {
          orgId: id,
          activeOrganizationId: this.props.activeOrganizationId,
          awardLeaderId: this.props.user.personId
        })

        if (config.updateTasks || config.updateDetail) {
          this.props.modal(null)
        }
      }

      handleSetLeaderSelectedTo = (id, newVal) => {
        const ids = this.props.leaders.selectedIds || []
        const newIds = ids.filter(i => i !== id).concat(newVal ? [id] : [])

        this.props.setLeadersSelected(newIds)
      }

      render() {
        const props = {
          ...this.props,
          local: undefined
        }
        const { leaders } = this.props

        return (
          <ComposedComponent
            {...props}
            findLeader={this.handleFindLeader}
            exportLeaders={this.handleExportLeaders}
            updateLeader={this.handleLeaderUpdateBlob}
            deleteLeader={this.handleLeaderDelete}
            addTraining={this.handleAddTraining}
            updateTraining={this.handleUpdateTraining}
            deleteTraining={this.handleDeleteTraining}
            addHistoryRecord={this.handlAddHistoryRecord}
            updateHistoryRecord={this.handlUpdateHistoryRecord}
            deleteHistoryRecord={this.handlDeleteHistoryRecord}
            moveLeader={this.handleMoveLeader}
            sendConfirmationEmail={this.handleSendConfirmatioEmail}
            transferLeaderParticipants={this.handleTransferLeaderParticipants}
            approveAwardLeaderRegistration={
              this.handleApproveAwardLeaderRegistration
            }
            returnAwardLeaderRegistration={
              this.handleReturnAwardLeaderRegistration
            }
            getLeaders={(id, options) =>
              this.handleGetLeaders(
                leaders,
                this.handleGetBaseFilter(id),
                options
              )}
            getCustomLeaders={(id, customLeaders, options) =>
              this.handleGetLeadersForSearch(
                customLeaders,
                this.handleGetBaseFilter(id),
                options)}
            resetLeaderDetail={this.handleResetLeaderDetail}
            setLeaderSelectedTo={this.handleSetLeaderSelectedTo}
          />
        )
      }
    }

    return enhance(Leaders)
  }
}

export default decorator
